import React, { useState, useCallback, useRef } from 'react';
import {
  KEYBOARD_LAYOUT,
  CONSONANT_MAP,
  getVowelVariants,
} from '../geezData';

const LONG_PRESS_DURATION = 400;
const VOWEL_VARIANT_TIMEOUT = 10000; // Extended to 10 seconds

export default function Keyboard({ onCharInput, onDelete, onSpace, onEnter, onSpacersChange }) {
  const [longPressChar, setLongPressChar] = useState(null);
  const [pressPosition, setPressPosition] = useState({ x: 0, y: 0 });
  const [cursorInVariant, setCursorInVariant] = useState(null);
  const longPressTimer = useRef(null);
  const pressedChar = useRef(null);
  const variantTimeout = useRef(null);
  const variantContainerRef = useRef(null);

  const handlePointerDown = useCallback((char, e) => {
    e.preventDefault();
    pressedChar.current = char;

    const rect = e.currentTarget.getBoundingClientRect();

    longPressTimer.current = setTimeout(() => {
      const clickX = rect.left + rect.width / 2;
      const clickY = rect.top + rect.height / 2;

      const viewportWidth = window.innerWidth;
      
      // Calculate the actual visual outer radius of the ring segments
      const variantsList = getVowelVariants(char);
      const n = variantsList.length;
      const gap = 14;
      const trapH = 50;
      const rInner = (n * (36 + gap)) / (2 * Math.PI);
      const rOuter = rInner + trapH;
      
      // Add a slight width padding (e.g., 16px) past the actual drawn bounds
      const activeRadius = Math.ceil(rOuter) + 16;

      const leftOverflow = Math.max(0, activeRadius - clickX);
      const rightOverflow = Math.max(0, (clickX + activeRadius) - viewportWidth);

      let finalX = clickX;

      if (leftOverflow > 0) {
        if (onSpacersChange) {
          onSpacersChange({ left: leftOverflow, right: 0 });
        }
        finalX = clickX + leftOverflow;
      } else if (rightOverflow > 0) {
        if (onSpacersChange) {
          onSpacersChange({ left: 0, right: rightOverflow });
        }
        finalX = clickX - rightOverflow;
      } else {
        if (onSpacersChange) {
          onSpacersChange({ left: 0, right: 0 });
        }
      }

      setPressPosition({
        x: finalX,
        y: clickY,
      });

      setLongPressChar(char);
      setCursorInVariant(null);

      // Auto-dismiss variants after timeout
      variantTimeout.current = setTimeout(() => {
        setLongPressChar(null);
        if (onSpacersChange) {
          onSpacersChange({ left: 0, right: 0 });
        }
      }, VOWEL_VARIANT_TIMEOUT);
    }, LONG_PRESS_DURATION);
  }, [onSpacersChange]);

  const handlePointerUp = useCallback((char, e) => {
    e.preventDefault();
    e.stopPropagation();
    clearTimeout(longPressTimer.current);

    if (!longPressChar) {
      onCharInput(char);
    }
    pressedChar.current = null;
  }, [longPressChar, onCharInput]);

  const handlePointerLeave = useCallback((char, e) => {
    clearTimeout(longPressTimer.current);
  }, []);

  const handleVariantSelect = useCallback((variant) => {
    onCharInput(variant.char);
    setLongPressChar(null);
    if (onSpacersChange) {
      onSpacersChange({ left: 0, right: 0 });
    }
    clearTimeout(variantTimeout.current);
  }, [onCharInput, onSpacersChange]);

  const handleVariantHover = useCallback((variant) => {
    setCursorInVariant(variant);
    clearTimeout(variantTimeout.current);
  }, []);

  const handleOverlayClose = useCallback(() => {
    setLongPressChar(null);
    if (onSpacersChange) {
      onSpacersChange({ left: 0, right: 0 });
    }
    clearTimeout(variantTimeout.current);
  }, [onSpacersChange]);

  // Get vowel variants for the currently long-pressed character
  const variants = longPressChar ? getVowelVariants(longPressChar) : [];
  const baseInfo = longPressChar ? CONSONANT_MAP[longPressChar] : null;

  return (
    <div className="keyboard-container">
      {KEYBOARD_LAYOUT.map((row, rowIdx) => (
        <div key={rowIdx} className="keyboard-row">
          {row.map(char => {
            const c = CONSONANT_MAP[char];
            const isPressed = longPressChar === char;
            return (
              <button
                key={char}
                className={`key-btn ${isPressed ? 'key-pressed' : ''}`}
                onPointerDown={(e) => handlePointerDown(char, e)}
                onPointerUp={(e) => handlePointerUp(char, e)}
                onPointerLeave={(e) => handlePointerLeave(char, e)}
                title={c ? `${c.name} - ${c.ipa}` : char}
              >
                <span className="key-char">{char}</span>
                <span className="key-name">{c?.ipa || ''}</span>
              </button>
            );
          })}
        </div>
      ))}

      {/* Controls row with added Ge'ez Full Stop (።) */}
      <div className="keyboard-row controls-row">
        <button className="key-btn ctrl-btn delete" onClick={onDelete} title="Delete">
          ⌫
        </button>
        <button className="key-btn ctrl-btn fullstop" onClick={() => onCharInput('።')} title="Full Stop (።)">
          ።
        </button>
        <button className="key-btn ctrl-btn space" onClick={onSpace} title="Space">
          ፡ <i className="space-sublabel">(Space)</i>
        </button>
        <button className="key-btn ctrl-btn enter" onClick={onEnter} title="Enter">
          ⏎
        </button>
      </div>

      {/* Long-press vowel variants - Dynamic Sun Ring with Parallel Slits */}
      {longPressChar && baseInfo && (() => {
        const variants = longPressChar ? getVowelVariants(longPressChar) : [];
        const n = variants.length;
        const gap = 14;
        const trapH = 50;
        const cx = 160;
        const cy = 160;
        const cornerRadius = 6;
        
        // Calculate inner radius based on number of segments
        const rInner = (n * (36 + gap)) / (2 * Math.PI);
        const rOuter = rInner + trapH;
        const totalSlice = (2 * Math.PI) / n;
        
        // Angular calculations
        const dAlphaInner = cornerRadius / rInner;
        const dAlphaOuter = cornerRadius / rOuter;
        const angularGapInner = gap / rInner;
        const angleWidthInner = totalSlice - angularGapInner;
        const angularGapOuter = gap / rOuter;
        const angleWidthOuter = totalSlice - angularGapOuter;
        
        // Calculate dynamic circle diameter
        const dCircle = 2 * rInner - 16;
        
        return (
          <div
            className="variant-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleOverlayClose();
              }
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div
              className="sun-ring-wrapper"
              ref={variantContainerRef}
              style={{
                left: `${pressPosition.x}px`,
                top: `${pressPosition.y}px`,
                '--n': n,
                '--d-circle': `${dCircle}px`,
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
            >
              {/* SVG Trapezoid Segments */}
              {variants.map((v, i) => {
                const baseAngle = i * totalSlice - Math.PI / 2;
                const isHovered = cursorInVariant?.char === v.char;
                
                // Angular positions
                const aStartInner = baseAngle + angularGapInner / 2;
                const aEndInner = aStartInner + angleWidthInner;
                const aStartOuter = baseAngle + angularGapOuter / 2;
                const aEndOuter = aStartOuter + angleWidthOuter;
                
                // Corner points
                const x1 = cx + rInner * Math.cos(aStartInner);
                const y1 = cy + rInner * Math.sin(aStartInner);
                const x2 = cx + rInner * Math.cos(aEndInner);
                const y2 = cy + rInner * Math.sin(aEndInner);
                const x3 = cx + rOuter * Math.cos(aEndOuter);
                const y3 = cy + rOuter * Math.sin(aEndOuter);
                const x4 = cx + rOuter * Math.cos(aStartOuter);
                const y4 = cy + rOuter * Math.sin(aStartOuter);
                
                // Rounded corner points
                const c1_in_x = cx + rInner * Math.cos(aStartInner + dAlphaInner);
                const c1_in_y = cy + rInner * Math.sin(aStartInner + dAlphaInner);
                const c1_out_x = cx + (rInner + cornerRadius) * Math.cos(aStartInner);
                const c1_out_y = cy + (rInner + cornerRadius) * Math.sin(aStartInner);
                
                const c2_in_x = cx + (rInner + cornerRadius) * Math.cos(aEndInner);
                const c2_in_y = cy + (rInner + cornerRadius) * Math.sin(aEndInner);
                const c2_out_x = cx + rInner * Math.cos(aEndInner - dAlphaInner);
                const c2_out_y = cy + rInner * Math.sin(aEndInner - dAlphaInner);
                
                const c3_in_x = cx + rOuter * Math.cos(aEndOuter - dAlphaOuter);
                const c3_in_y = cy + rOuter * Math.sin(aEndOuter - dAlphaOuter);
                const c3_out_x = cx + (rOuter - cornerRadius) * Math.cos(aEndOuter);
                const c3_out_y = cy + (rOuter - cornerRadius) * Math.sin(aEndOuter);
                
                const c4_in_x = cx + (rOuter - cornerRadius) * Math.cos(aStartOuter);
                const c4_in_y = cy + (rOuter - cornerRadius) * Math.sin(aStartOuter);
                const c4_out_x = cx + rOuter * Math.cos(aStartOuter + dAlphaOuter);
                const c4_out_y = cy + rOuter * Math.sin(aStartOuter + dAlphaOuter);
                
                // Build SVG path with rounded corners
                const d = `
                  M ${c1_in_x.toFixed(2)} ${c1_in_y.toFixed(2)}
                  A ${rInner} ${rInner} 0 0 1 ${c2_out_x.toFixed(2)} ${c2_out_y.toFixed(2)}
                  Q ${x2.toFixed(2)} ${y2.toFixed(2)} ${c2_in_x.toFixed(2)} ${c2_in_y.toFixed(2)}
                  L ${c3_out_x.toFixed(2)} ${c3_out_y.toFixed(2)}
                  Q ${x3.toFixed(2)} ${y3.toFixed(2)} ${c3_in_x.toFixed(2)} ${c3_in_y.toFixed(2)}
                  A ${rOuter} ${rOuter} 0 0 0 ${c4_out_x.toFixed(2)} ${c4_out_y.toFixed(2)}
                  Q ${x4.toFixed(2)} ${y4.toFixed(2)} ${c4_in_x.toFixed(2)} ${c4_in_y.toFixed(2)}
                  L ${c1_out_x.toFixed(2)} ${c1_out_y.toFixed(2)}
                  Q ${x1.toFixed(2)} ${y1.toFixed(2)} ${c1_in_x.toFixed(2)} ${c1_in_y.toFixed(2)}
                  Z
                `;
                
                // Calculate text position at center of trapezoid
                const midAngle = baseAngle + totalSlice / 2;
                const midRadius = rInner + trapH / 2;
                const charX = cx + midRadius * Math.cos(midAngle);
                const charY = cy + midRadius * Math.sin(midAngle);
                
                const fillColor = i % 2 === 0 ? 'var(--bg-tertiary)' : 'var(--bg-surface)';
                
                return (
                  <svg
                    key={v.char}
                    className="trapezoid-svg"
                    viewBox="0 0 320 320"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVariantSelect(v);
                    }}
                    onPointerEnter={() => handleVariantHover(v)}
                    onPointerLeave={() => setCursorInVariant(null)}
                    title={`${v.vowelLabel} - ${v.fullIPA}`}
                  >
                    <path
                      d={d}
                      fill={isHovered ? 'var(--bg-elevated)' : fillColor}
                      stroke="var(--accent-glow)"
                      strokeWidth="1"
                    />
                    <text
                      className="key-char"
                      x={charX}
                      y={charY - 10}
                      fill="var(--text-primary)"
                      fontSize="1.4rem"
                      fontWeight="bold"
                      textAnchor="middle"
                      dominantBaseline="central"
                      pointerEvents="none"
                    >
                      {v.char}
                    </text>
                    <text
                      className="key-name"
                      x={charX}
                      y={charY + 10}
                      fill="var(--text-muted)"
                      fontSize="0.8rem"
                      textAnchor="middle"
                      dominantBaseline="central"
                      pointerEvents="none"
                    >
                      {v.ipa}
                    </text>
                  </svg>
                );
              })}

              {/* Center: pressed character (circular OK button) */}
              <button
                className="sun-ring-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleVariantSelect({ char: longPressChar, vowelLabel: baseInfo.name, fullIPA: baseInfo.ipa });
                }}
                title={`${baseInfo.name} - ${baseInfo.ipa}`}
              >
                <span className="key-char">{longPressChar}</span>
                <span className="key-name">{baseInfo.name}</span>
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}